import React, { createContext, forwardRef, useContext, useId, useMemo, useState } from "react";

import eyeOffIcon from "../../assets/svg/eye-off.svg";
import eyeOpenIcon from "../../assets/svg/eye-open.svg";
import ButtonUI from "./ButtonUI";
import { pressPoint } from "./glass";
import { cornerClass, joinedCorners, useGridColumns } from "./joined";

const cx = (...a) => a.filter(Boolean).join(" ");

// grupul spune copiilor unde stă eticheta (un copil o poate schimba cu props-ul lui)
const GroupContext = createContext(null);

// elementele care au clicul lor în plăcuța apăsabilă (copiază, comutatorul)
const INNER_CONTROL = "button, a, input, select, textarea, [role='button'], [role='switch']";

/**
 * InputUI — singurul câmp al aplicației. Înlocuiește plăcuța din popup-uri
 * (Field / FieldInput / FieldSelect / FieldTextarea / FieldText,
 * createReservationPopup__field, editReservationPopup__field, profilurile),
 * câmpul cu contur din paginile de admin (OutlinedInput + LabeledField:
 * auditLog__input, messageTemplates__input, exam-admin__input), parola cu
 * ochi (sign__input-wrapper + sign__eye-btn), câmpul din Înscriere
 * (sign__field + sign__label) și PersonField. Stilurile: scss/UI/_inputUI.scss.
 *
 * label: eticheta · labelPosition: inside (implicit: plăcuța --black-p cu
 *        eticheta mică cu majuscule înăuntru) | above (câmpul cu contur, eticheta
 *        de 12px deasupra); în InputUI.Group vine de la grup
 * as: input (implicit) | select | textarea | text (doar afișează `value`, ca FieldText)
 * type: tipul câmpului (text, password, number, date, tel, email…);
 *       password primește ochiul care arată / ascunde parola
 * revealed / onRevealChange: parola arătată, ținută de afară (un singur ochi
 *       pentru toate parolele din formular, ca la Creare cont)
 * options: [{ value, label, disabled }] sau texte, pentru select (ori copii <option>)
 * value: valoarea câmpului; la as="text" e textul afișat (sau children)
 * detail: al doilea rând la as="text" (telefonul de sub nume) · detailAfter: ce stă la capătul lui
 * before / after: ce stă în câmp înainte / după valoare (ex. „copiază” =
 *       <ButtonUI variant="ghost" size="sm" icon="copy" aria-label="Copiază" />, un comutator).
 *       O plăcuță as="text" fără valoare, doar cu eticheta și after, se așază
 *       pe un rând: eticheta în stânga, comutatorul în dreapta (Field toggle)
 * onClick (cu as="text"): plăcuța apăsabilă (PersonField: deschide căutarea);
 *       role="button", se apasă și cu Enter / Space; clicurile pe butoanele din
 *       ea (copiază) nu o apasă
 * hint: textul mic de sub câmp · error: mesajul de eroare (--red-text, aria-invalid)
 * disabled · required (trece pe câmp; aplicația n-are un semn vizual pentru el)
 * selected: plăcuța aleasă (fundal accent, ca elevul ales în Programare nouă)
 * full: pe tot rândul într-un grup cu mai multe coloane
 * id: legat de <label htmlFor> (se face singur dacă lipsește)
 * className, style: pe rădăcină; restul props-urilor (placeholder, onChange,
 *       name, autoComplete, rows, min, inputMode…) merg pe <input> / <select> /
 *       <textarea>; ref ajunge la câmp. La as="text" restul și ref-ul merg pe plăcuță.
 * Pe alt fundal decât cel obișnuit (contur pe --black-s, plăcuța pe --black-p)
 * schimbi variabila CSS --inputUI-bg (pe câmp, pe grup sau pe panou).
 */
const InputUI = forwardRef(function InputUI(
   {
      as = "input",
      label,
      labelPosition,
      type = "text",
      revealed,
      onRevealChange,
      options,
      value,
      detail,
      detailAfter,
      before,
      after,
      onClick,
      hint,
      error,
      disabled = false,
      required = false,
      selected = false,
      full = false,
      corners,
      id,
      className = "",
      style,
      children,
      ...rest
   },
   ref,
) {
   const group = useContext(GroupContext);
   const autoId = useId();
   const [ownRevealed, setOwnRevealed] = useState(false);

   const position = labelPosition || group?.labelPosition || "inside";
   const above = position === "above";
   const isText = as === "text";
   const clickable = isText && typeof onClick === "function";
   const isPassword = as === "input" && type === "password";
   const shown = revealed ?? ownRevealed;
   const textValue = value ?? children;
   const hasLabel = label != null && label !== false;
   // eticheta + comutatorul pe un rând (createReservationPopup__field--toggle)
   const inline = isText && textValue == null && detail == null && after != null;

   const fieldId = id || `inputUI${autoId.replace(/:/g, "")}`;
   const hintId = hint ? `${fieldId}-hint` : undefined;
   const errorId = error ? `${fieldId}-error` : undefined;
   const describedBy = cx(rest["aria-describedby"], hintId, errorId) || undefined;

   const toggleReveal = () => {
      const next = !shown;
      if (revealed == null) setOwnRevealed(next);
      onRevealChange?.(next);
   };

   // ochiul parolei (sign__eye-btn): butonul-iconiță fără fundal; arată / ascunde parola
   // din câmpul ăsta sau, cu revealed, toate parolele care primesc aceeași stare
   const eye = isPassword ? (
      <ButtonUI
         variant="ghost"
         size="sm"
         icon={shown ? eyeOffIcon : eyeOpenIcon}
         className="inputUI__eye"
         aria-label={shown ? "Ascunde parola" : "Arată parola"}
         title={shown ? "Ascunde parola" : "Arată parola"}
         aria-controls={fieldId}
         disabled={disabled}
         onClick={toggleReveal}
      />
   ) : null;

   const fieldProps = {
      ...rest,
      id: fieldId,
      className: "inputUI__control",
      disabled,
      required,
      "aria-invalid": error ? true : rest["aria-invalid"],
      "aria-describedby": describedBy,
   };
   if (onClick && !isText) fieldProps.onClick = onClick;

   let control = null;
   if (isText) {
      control = textValue != null && <span className="inputUI__text">{textValue}</span>;
   } else if (as === "select") {
      control = (
         <select ref={ref} value={value} {...fieldProps}>
            {options
               ? options.map((o) => {
                    const opt = o !== null && typeof o === "object" ? o : { value: o, label: o };
                    return (
                       <option key={String(opt.value)} value={opt.value} disabled={opt.disabled}>
                          {opt.label ?? opt.value}
                       </option>
                    );
                 })
               : children}
         </select>
      );
   } else if (as === "textarea") {
      control = <textarea ref={ref} value={value} {...fieldProps} />;
   } else {
      control = <input ref={ref} type={isPassword && shown ? "text" : type} value={value} {...fieldProps} />;
   }

   const afterSlot = after != null || eye ? (
      <span className="inputUI__after">
         {after}
         {eye}
      </span>
   ) : null;

   const LabelTag = isText ? "span" : "label";

   // plăcuța apăsabilă: clicul și Enter / Space, dar nu din butoanele dinăuntru
   const pressProps = clickable
      ? {
           role: "button",
           tabIndex: disabled ? -1 : 0,
           "aria-disabled": disabled || undefined,
           onClick: (e) => {
              const inner = e.target.closest?.(INNER_CONTROL);
              if (disabled || (inner && inner !== e.currentTarget)) return;
              onClick(e);
           },
           onKeyDown: (e) => {
              if (disabled || e.target !== e.currentTarget) return;
              if (e.key === "Enter" || e.key === " ") {
                 e.preventDefault();
                 onClick(e);
              }
           },
        }
      : {};

   return (
      <div
         ref={isText ? ref : undefined}
         className={cx(
            "inputUI",
            above && "inputUI--above",
            as !== "input" && `inputUI--${as}`,
            clickable && "inputUI--clickable",
            inline && "inputUI--inline",
            full && "inputUI--full",
            selected && "is-selected",
            disabled && "is-disabled",
            error && "is-invalid",
            cornerClass(corners),
            className,
         )}
         style={style}
         // lumina de la apăsare pornește din punctul atins (la eticheta deasupra: pe chenar)
         onPointerDown={(e) => pressPoint(e, above ? e.currentTarget.querySelector(".inputUI__line") : undefined)}
         {...(isText ? { ...rest, "aria-describedby": describedBy } : null)}
         {...pressProps}
      >
         {hasLabel && (
            <LabelTag className="inputUI__label" htmlFor={isText ? undefined : fieldId}>
               {label}
            </LabelTag>
         )}
         <div className="inputUI__line">
            {before != null && <span className="inputUI__before">{before}</span>}
            {control}
            {afterSlot}
         </div>
         {isText && detail != null && (
            <div className="inputUI__line">
               <span className="inputUI__text">{detail}</span>
               {detailAfter != null && <span className="inputUI__after">{detailAfter}</span>}
            </div>
         )}
         {hint && (
            <p id={hintId} className="inputUI__hint">
               {hint}
            </p>
         )}
         {error && (
            <p id={errorId} className="inputUI__error">
               {error}
            </p>
         )}
      </div>
   );
});

/**
 * InputUI.Group — câmpurile puse împreună. Cu eticheta înăuntru (implicit):
 * plăcuțele lipite la 3px într-un grup cu colțuri de 24px
 * (createReservationPopup__group-body + __form-row). Cu labelPosition="above":
 * o grilă la 8px × 12px, fără lipire (auditLog__row).
 *
 * columns: 1 (implicit) | 2 | 3 — coloane egale (3: pe telefon una sub alta)
 * labelPosition: inside | above — și pentru câmpurile din grup
 * Un grup în alt grup e un rând al lui (ca FieldRow): fără colțuri proprii, pe
 * toată lățimea — așa se pun rânduri cu număr diferit de coloane.
 * Colțurile: plăcuțele de la marginea grupului își rotunjesc singure colțurile de
 * afară la 24px (după coloanele de fapt, și pe telefon); grupul nu taie nimic. Un rând
 * primește colțurile de la grupul părinte (corners) și le dă plăcuțelor lui.
 */
function InputGroup({ columns = 1, labelPosition, corners: edges, className = "", style, children, ...rest }) {
   const parent = useContext(GroupContext);
   const position = labelPosition || parent?.labelPosition || "inside";
   const ctx = useMemo(() => ({ labelPosition: position }), [position]);
   const [measured, gridRef] = useGridColumns(Math.max(1, columns));
   const kids = React.Children.toArray(children);
   const isTile = (c) => React.isValidElement(c) && (c.type === InputUI || c.type === InputGroup);
   const spans = kids.filter(isTile).map((c) => (c.type === InputGroup || c.props.full ? "full" : 1));
   const corners = position === "above" ? null : joinedCorners(spans, measured, edges);
   let tile = 0;
   const content = kids.map((c) => {
      if (!isTile(c) || !corners) return c;
      const k = tile;
      tile += 1;
      return React.cloneElement(c, { corners: corners[k] });
   });
   return (
      <GroupContext.Provider value={ctx}>
         <div
            ref={gridRef}
            className={cx(
               "inputUIGroup",
               position === "above" && "inputUIGroup--above",
               columns >= 3 && "inputUIGroup--stackMobile",
               className,
            )}
            style={{ "--inputUIGroup-cols": Math.max(1, columns), ...style }}
            {...rest}
         >
            {content}
         </div>
      </GroupContext.Provider>
   );
}

InputUI.Group = InputGroup;

export default InputUI;
